// מסך 8 — מרכז בדיקות ויצוא: לכל וריאציה בסט — בדיקות, אישור אזהרות, סיכום קישורים, יצוא והיסטוריה; יצוא סט שלם.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, waitForJob, type Job } from '../api';
import { Empty, ErrorBox, Field, JobBadge, Loading, useLoad, useMe } from '../components/ui';
import VariantExportCard, { type LinkRow } from '../components/exportc/VariantExportCard';
import { KIND_LABEL } from '../components/exportc/ExportResult';
import '../editor.css';

interface SetResp { set: { id: string; name: string; campaign_id: string }; variants: any[] }

export default function ExportCenter() {
  const [sp] = useSearchParams();
  const setId = sp.get('setId');
  const variantId = sp.get('variantId');
  const [resolvedSet, setResolvedSet] = useState<string | null>(setId);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setErr(null);
    if (setId) { setResolvedSet(setId); return; }
    if (variantId) api.get<any>(`/api/variants/${variantId}`).then((c) => setResolvedSet(c.variant.banner_set_id)).catch((e) => setErr(e.message));
    else setResolvedSet(null);
  }, [setId, variantId]);
  if (err) return <ErrorBox error={err} />;
  if (!setId && !variantId) return <Picker />;
  if (!resolvedSet) return <Loading />;
  return <SetExports key={resolvedSet} setId={resolvedSet} focus={variantId} />;
}

function Picker() {
  const nav = useNavigate();
  const camps = useLoad<any[]>('/api/campaigns?limit=200');
  const [cid, setCid] = useState('');
  const camp = useLoad<any>(cid ? `/api/campaigns/${cid}` : null);
  // קמפיין אחד בלבד → נבחר מראש; קמפיין עם סט אחד → נכנסים אליו ישר
  useEffect(() => { if (!cid && camps.data?.length === 1) setCid(camps.data[0].id); }, [camps.data, cid]);
  useEffect(() => { if (camp.data?.sets.length === 1) nav(`/exports?setId=${camp.data.sets[0].id}`, { replace: true }); }, [camp.data, nav]);
  return (
    <div>
      <div className="page-title"><div><h1>בדיקות ויצוא</h1><p>בוחרים קמפיין, בודקים שכל הבאנרים תקינים, ומורידים אותם לשליחה לאתר.</p></div></div>
      <div className="card">
        <Field label="קמפיין">
          <select value={cid} onChange={(e) => setCid(e.target.value)}>
            <option value="">בחרו קמפיין…</option>
            {camps.data?.map((c) => <option key={c.id} value={c.id}>{c.client_name} · {c.name}</option>)}
          </select>
        </Field>
        <ErrorBox error={camps.error ?? camp.error} />
        {camp.data && (camp.data.sets.length === 0 ? <Empty>עדיין אין באנרים בקמפיין הזה. <Link to={`/sets/new?campaignId=${cid}`}>יצירת סט באנרים</Link></Empty> : (
          <ul>{camp.data.sets.map((s: any) => <li key={s.id}><button className="linkish" onClick={() => nav(`/exports?setId=${s.id}`)}>{s.name}</button> <span className="small muted">({s.variant_count} גדלים)</span></li>)}</ul>
        ))}
      </div>
    </div>
  );
}

function SetExports({ setId, focus }: { setId: string; focus: string | null }) {
  const me = useMe();
  const data = useLoad<SetResp>(`/api/banner-sets/${setId}`);
  const [links, setLinks] = useState<Record<string, LinkRow> | null>(null);
  const [linksErr, setLinksErr] = useState<string | null>(null);
  const [warn, setWarn] = useState<Record<string, string[]>>({});
  const [showAll, setShowAll] = useState(!focus);

  useEffect(() => {
    if (!data.data) return;
    api.get<{ rows: LinkRow[] } | LinkRow[]>(`/api/campaigns/${data.data.set.campaign_id}/tracking-links`)
      .then((r) => { const rows = Array.isArray(r) ? r : r.rows; setLinks(Object.fromEntries(rows.map((x) => [x.variantId, x]))); })
      .catch((e) => setLinksErr(e.message));
  }, [data.data]);
  const onWarnings = useCallback((vid: string, codes: string[]) => setWarn((w) => ({ ...w, [vid]: codes })), []);

  if (data.error) return <ErrorBox error={data.error} />;
  if (!data.data) return <Loading />;
  const { set, variants } = data.data;
  const shown = showAll || !focus ? variants : variants.filter((v) => v.id === focus);

  return (
    <div className="stack">
      <div className="topbar">
        <div>
          <h1 style={{ marginBottom: 2 }}>בדיקות ויצוא — {set.name}</h1>
          <div className="row small">
            <Link to={`/sets/${set.id}`}>לסט</Link>
            <Link to={`/campaigns/${set.campaign_id}`}>לקמפיין</Link>
            <Link to={`/campaigns/${set.campaign_id}/links`}>קישורים מתויגים</Link>
          </div>
        </div>
        {focus && <button className="btn" onClick={() => setShowAll((x) => !x)}>{showAll ? 'הצג רק את הווריאציה שנבחרה' : `הצג את כל הסט (${variants.length})`}</button>}
      </div>

      <section className="card" aria-labelledby="links-h">
        <h2 id="links-h" style={{ marginTop: 0 }}>סיכום קישורים לפני יצוא</h2>
        {linksErr && <div className="alert warn small">טבלת הקישורים המתויגים אינה זמינה ({linksErr}). הקישור הנעול לכל וריאציה מוצג בכרטיס מתוצאת הבדיקה.</div>}
        {!links && !linksErr && <Loading />}
        {links && (
          <table className="table">
            <thead><tr><th>גודל</th><th>מדיה · מיקום</th><th>כתובת סופית</th><th>מצב</th></tr></thead>
            <tbody>
              {variants.map((v) => { const l = links[v.id]; return (
                <tr key={v.id}>
                  <td className="mono">{v.width}×{v.height}</td>
                  <td>{v.media_name} · {v.placement_name}</td>
                  <td className="mono small" style={{ wordBreak: 'break-all' }}>{l?.resolvedUrl ?? <span className="muted">—</span>}</td>
                  <td>{l ? <span className={`badge ${l.status === 'ok' ? 'ok' : 'warn'}`}>{l.status}</span> : <span className="badge warn">לא נוצר</span>}</td>
                </tr>
              ); })}
            </tbody>
          </table>
        )}
      </section>

      <SetExportPanel setId={setId} variants={variants} warn={warn} canDelivery={me.canExportDelivery} />

      {shown.map((v) => <VariantExportCard key={v.id} variant={v} link={links?.[v.id] ?? null} onWarnings={onWarnings} />)}
    </div>
  );
}

function SetExportPanel({ setId, variants, warn, canDelivery }: { setId: string; variants: any[]; warn: Record<string, string[]>; canDelivery: boolean }) {
  const [kind, setKind] = useState<'draft' | 'delivery' | 'test'>('draft');
  const allWarn = useMemo(() => [...new Set(Object.values(warn).flat())].sort(), [warn]);
  const [acks, setAcks] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<any[] | null>(null);
  const [jobs, setJobs] = useState<Record<string, Job>>({});
  const [finals, setFinals] = useState<Record<string, any>>({});
  const pendingKey = useRef<Record<string, string>>({});

  const run = async () => {
    setBusy(true); setErr(null); setRows(null); setJobs({}); setFinals({});
    try {
      const sig = `${kind}|${[...acks].sort().join(',')}`;
      const key = (pendingKey.current[sig] ??= crypto.randomUUID());
      const r = await api.post<{ jobIds: string[]; results: any[] }>(`/api/banner-sets/${setId}/export`, { kind, ackWarnings: acks, idempotencyKey: key });
      delete pendingKey.current[sig];
      setRows(r.results);
      await Promise.all(r.results.filter((x) => x.ok && x.jobId).map(async (x) => {
        try {
          await waitForJob(x.jobId, (j) => setJobs((m) => ({ ...m, [x.variantId]: j })));
          const e = await api.get<any>(`/api/exports/${x.exportId}`);
          setFinals((m) => ({ ...m, [x.variantId]: e }));
        } catch (e: any) { setFinals((m) => ({ ...m, [x.variantId]: { status: 'failed', error: e.message } })); }
      }));
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <section className="card" aria-labelledby="setx-h">
      <h2 id="setx-h" style={{ marginTop: 0 }}>יצוא הסט כולו ({variants.length} גדלים)</h2>
      <div className="row">
        <Field label="סוג">
          <select value={kind} onChange={(e) => setKind(e.target.value as any)}>
            <option value="draft">טיוטה (מסומן)</option>
            <option value="delivery" disabled={!canDelivery}>מסירה{!canDelivery ? ' — אין הרשאה' : ''}</option>
            <option value="test">חבילת טסט</option>
          </select>
        </Field>
        <button className="btn primary" onClick={run} disabled={busy}>{busy ? 'מייצא…' : `ייצא את כל הגדלים כ${KIND_LABEL[kind]}`}</button>
      </div>
      {allWarn.length > 0 && (
        <fieldset className="pgroup" style={{ marginTop: 8 }}>
          <legend>אישור אזהרות לכל הסט (יתועד)</legend>
          {allWarn.map((c) => <label key={c} className="chk" style={{ display: 'flex' }}><input type="checkbox" checked={acks.includes(c)} onChange={(e) => setAcks((a) => e.target.checked ? [...a, c] : a.filter((x) => x !== c))} /> <span className="mono">{c}</span> <span className="small muted">({Object.entries(warn).filter(([, cs]) => cs.includes(c)).length} גדלים)</span></label>)}
        </fieldset>
      )}
      <p className="small muted">שגיאה חוסמת מסירה בכל גודל בנפרד; גודל שנכשל אינו מבטל את האחרים.</p>
      <ErrorBox error={err} />
      {rows && (
        <table className="table" aria-live="polite">
          <thead><tr><th>גודל</th><th>מצב</th><th>חבילה</th><th>פרטים</th></tr></thead>
          <tbody>
            {rows.map((x) => { const f = finals[x.variantId]; const j = jobs[x.variantId]; return (
              <tr key={x.variantId}>
                <td className="mono">{x.width}×{x.height}</td>
                <td>{!x.ok ? <span className="badge err">נכשל</span> : f ? <span className={`badge ${f.status === 'ready' ? 'ok' : 'err'}`}>{f.status === 'ready' ? 'מוכן' : f.status === 'blocked' ? 'חסום' : f.status}</span> : j ? <JobBadge status={j.status} /> : <span className="badge info">בתור</span>}</td>
                <td className="mono small" style={{ wordBreak: 'break-all' }}>{f?.package_name ?? ''}</td>
                <td className="small">{x.error ?? f?.error ?? (f?.manifest?.gate?.reasons ?? []).join(' · ')}</td>
              </tr>
            ); })}
          </tbody>
        </table>
      )}
    </section>
  );
}
